import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'
import { Trans } from 'react-i18next'
import styled from 'styled-components'

import OrderSummaryPriceBreakDown from 'checkout/components/OrderSummaryPanel/OrderSummaryPriceBreakDown'
import { useOrderSummaryContext } from 'checkout/containers/OrderSummaryContextProvider'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { t } from 'localization'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { RotatingCaret } from 'shared/components'
import { Button } from 'shared/components/Button'
import Divider from 'shared/components/Divider'
import Image from 'shared/components/Image'
import { FCLink } from 'shared/components/Links'
import Spinner from 'shared/components/Spinner'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { CheckoutQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { colors, fonts } from 'shared/lib'
import {
  displayPriceHideCentsCurrency,
  displayPriceHideCentsIfZero,
} from 'shared/utils/currency-utils'

const TP = 'checkout.components.OrderSummaryPanel.OrderSummary'

const OrderSummary = () => {
  const tv = useTranslation()
  const {
    isFeeBreakdownOpen,
    order,
    orderReadyForPurchase,
    orderSubmitting,
    purchaseOrder,
    setIsFeeBreakdownOpen,
    showSpinner,
    spinnerText,
  } = useOrderSummaryContext()

  const { currencyCode } = useShoppingRegionContext()

  const {
    finalPrice,
    instantShipMarkupFeePrice,
    isInstantShip,
    listPrice,
    product: {
      size,
      mainPictureUrl,
      productTemplate: { details, name },
    },
    productPrice,
  } = order

  // #TODO: Remove Klarnas purchaseOrder logic below once express checkout is enabled for 4+ months
  const isKlarnaExpressEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_EXPRESS_KLARNA)
  const klarnaReason = new URLSearchParams(window.location.search).get('klarna')
  // I DONT LIKE THIS BUT LET ME KNOW IF YOU KNOW A BETTER WAY
  useEffect(() => {
    if (isKlarnaExpressEnabled) return

    switch (klarnaReason) {
      case 'success':
        if (orderReadyForPurchase && order?.billingInfo?.paymentType === 'klarna') {
          purchaseOrder()
        }
        break
      case 'failure':
        break
      case 'error':
        break
      case 'cancel':
        break
      case 'back':
        break
      default:
        break
    }
  }, [])

  useEffect(() => {
    if (orderReadyForPurchase) {
      sendTrackingEvent('CHECKOUT_VIEW', { request_type: 'gql', step: 'review' })
    }
  }, [orderReadyForPurchase])

  const isCheckoutMarketPricingEnabled = useFeatureFlag(FeatureFlag.WEB_FC_CHECKOUT_MARKET_PRICING)
  const klarnaDisablePlaceOrder = order?.billingInfo?.paymentType === 'klarna'
  const payPalv2DisablePlaceOrder = order?.billingInfo?.paymentType === 'paypal_v2'

  const showFeeBreakdown =
    isCheckoutMarketPricingEnabled && isInstantShip && !!instantShipMarkupFeePrice?.value

  return (
    <Spinner
      showSpinner={showSpinner}
      text={spinnerText}
      subText={t(`${TP}.spinnerSubText`, 'Please do not close browser!')}
    >
      <ProductSummary>
        <StyledImage
          src={mainPictureUrl}
          data-qa={CheckoutQA.OrderSummaryShoeImage}
          alt={name}
          width={77}
          height={77}
        />
        <ProductDescription>
          <ProductName data-qa={CheckoutQA.OrderSummaryShoeName}>{name}</ProductName>
          {size?.display && (
            <Flex>
              <Size data-qa={CheckoutQA.OrderSummaryShoeSize}>
                {t(`${TP}.productSize`, {
                  defaultValue: 'US Size {size}',
                  size: size.display,
                })}
              </Size>
            </Flex>
          )}
          <ProductDetailsLineTwo>
            <Flex>
              <ProductDetails data-qa={CheckoutQA.OrderSummaryShoeColorway}>
                {details}
              </ProductDetails>
            </Flex>

            <ProductPrice data-qa={CheckoutQA.OrderSummaryShoePrice}>
              {displayPriceHideCentsCurrency(productPrice.display, currencyCode)}
            </ProductPrice>
            {showFeeBreakdown && (
              <button
                data-qa={CheckoutQA.OrderSummaryBreakdownToggle}
                onClick={() => setIsFeeBreakdownOpen(!isFeeBreakdownOpen)}
              >
                <RotatingCaret color={colors.FC2_GREY} isOpen={isFeeBreakdownOpen} />
              </button>
            )}
          </ProductDetailsLineTwo>
        </ProductDescription>
      </ProductSummary>
      {showFeeBreakdown && isFeeBreakdownOpen && (
        <FeePrices>
          <PriceLine>
            <FeeDetailTitle data-qa={CheckoutQA.OrderSummaryListPriceTag}>
              {t(`${TP}.productPrice`, 'Product Price')}
            </FeeDetailTitle>
            <OrderSummaryCopy data-qa={CheckoutQA.OrderSummaryListPriceAmount}>
              {displayPriceHideCentsIfZero(listPrice.display)}
            </OrderSummaryCopy>
          </PriceLine>
          <PriceLine>
            <FeeDetailTitle data-qa={CheckoutQA.OrderSummaryPriorityProcessingFeeTag}>
              {t(`${TP}.priorityProcessing`, 'Priority Processing')}
            </FeeDetailTitle>
            <OrderSummaryCopy data-qa={CheckoutQA.OrderSummaryPriorityProcessingFeeAmount}>
              +{displayPriceHideCentsIfZero(instantShipMarkupFeePrice.display)}
            </OrderSummaryCopy>
          </PriceLine>
          <PriceDivider />
          <PriceLine>
            <FeeDetailTitle data-qa={CheckoutQA.OrderSummaryTotalPriceTag}>
              {t(`${TP}.totalPrice`, 'Total Price')}
            </FeeDetailTitle>
            <OrderSummaryCopy data-qa={CheckoutQA.OrderSummaryTotalPriceAmount}>
              {displayPriceHideCentsIfZero(productPrice.display)}
            </OrderSummaryCopy>
          </PriceLine>
        </FeePrices>
      )}
      <Divider />
      <OrderSummaryPriceBreakDown />
      <Divider />
      <PriceLine>
        <FinalPriceTitle data-qa={CheckoutQA.OrderSummaryOrderTotalTag}>
          {t(`${TP}.orderTotal`, 'Order Total')}
        </FinalPriceTitle>
        <FinalPriceValue data-qa={`OrderSummaryTotalAmount${CheckoutQA.FinalCheckout}`}>
          {displayPriceHideCentsCurrency(finalPrice.display, currencyCode)}
        </FinalPriceValue>
      </PriceLine>
      <TermsAndConditions data-qa={CheckoutQA.PlaceOrderTermsAndConditionsText}>
        <Trans
          i18nKey={`${TP}.withThreeRetail`}
          t={tv.t}
          defaults='By clicking "place order", I acknowledge that I have read and agree to the <0>Terms & Conditions</0> and the <1>Privacy Policy</1>.'
          components={[
            <FCLink
              href="/terms-conditions"
              newTab
              qaAttr={CheckoutQA.PlaceOrderTermsConditionsLink}
            />,
            <FCLink href="/privacy" newTab qaAttr={CheckoutQA.PlaceOrderPrivacyPolicyLink} />,
          ]}
        />
      </TermsAndConditions>
      <Button
        buttonType="primary2"
        disabled={
          orderSubmitting ||
          !orderReadyForPurchase ||
          klarnaDisablePlaceOrder ||
          payPalv2DisablePlaceOrder
        }
        onClick={purchaseOrder}
        type="button"
        $fill
        qaAttr={CheckoutQA.PlaceOrderButton}
      >
        {t(`${TP}.placeOrder`, 'PLACE ORDER')}
      </Button>
    </Spinner>
  )
}

export default OrderSummary

const Flex = styled.div`
  display: flex;
`

const OrderSummaryCopy = styled.p`
  margin: 0;
`

const ProductSummary = styled(Flex)`
  align-items: center;
`

const ProductDetails = styled(OrderSummaryCopy)`
  ${fonts.BODY_TEXT}
`

const ProductDescription = styled.div`
  flex-grow: 1;
  ${fonts.BODY_TEXT}
`

const Size = styled(OrderSummaryCopy)`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_GREY};
`

const FeePrices = styled.section`
  padding: 20px 20px 10px;
  margin: 20px 0;
  background-color: ${colors.FC2_OFF_WHITE};
`

const FeeDetailTitle = styled.span`
  margin-right: 5px;
`

const ProductDetailsLineTwo = styled(Flex)`
  margin-top: 5px;
  justify-content: space-between;
`

const ProductPrice = styled(OrderSummaryCopy)`
  ${fonts.SUBTITLE_3}
`

const ProductName = styled(OrderSummaryCopy)`
  ${fonts.SUBTITLE_2}
  font-weight: bold;
  margin: 5px 0;
`

const TermsAndConditions = styled.p`
  margin: 20px 0;
  ${fonts.BODY_TEXT}
`

const StyledImage = styled(Image)<{ src: string }>`
  background-image: url(${({ src }) => src});
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  width: 100%;
  min-height: 77px;
  max-width: 77px;
  margin-right: 12px;
  object-fit: contain;
`

const PriceLine = styled(Flex)`
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 10px;
  color: ${colors.FC2_DARK_GREY};
`

const PriceDivider = styled(Divider)`
  margin: 15px 0;
`

const FinalPriceTitle = styled.div`
  ${fonts.SUBTITLE_3}
`

const FinalPriceValue = styled.div`
  ${fonts.SUBTITLE_1}
`
